import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type { FirmSettings, StatementGap, DemandLetterMeta, DemandLetterSettings } from '../types';

export function useFirmSettings() {
  return useQuery<FirmSettings>({
    queryKey: ['firm-settings'],
    queryFn: async () => {
      const { data } = await api.get('/firm-settings');
      return data;
    },
  });
}

export function useUpdateFirmSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (settings: Partial<FirmSettings> & { clear_template?: boolean; clear_example_letter?: boolean }) => {
      const { data } = await api.put('/firm-settings', settings);
      return data as FirmSettings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firm-settings'] });
    },
  });
}

export function useUploadFirmTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/firm-settings/template', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firm-settings'] });
    },
  });
}

export function useUploadExampleLetter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/firm-settings/example-letter', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firm-settings'] });
    },
  });
}

export interface FirmDocumentExample {
  id: number;
  filename: string | null;
  file_kind: 'docx' | 'pdf' | null;
  is_primary: boolean;
  created_at: string | null;
}

export function useDocumentExamples(docType: string = 'demand_letter') {
  return useQuery<{ examples: FirmDocumentExample[] }>({
    queryKey: ['document-examples', docType],
    queryFn: async () => {
      const { data } = await api.get('/firm-settings/examples', { params: { doc_type: docType } });
      return data;
    },
  });
}

export function useAddDocumentExample(docType: string = 'demand_letter') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/firm-settings/examples', formData, {
        params: { doc_type: docType },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-examples', docType] });
    },
  });
}

export function useDeleteDocumentExample(docType: string = 'demand_letter') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (exampleId: number) => {
      await api.delete(`/firm-settings/examples/${exampleId}`);
      return exampleId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-examples', docType] });
    },
  });
}

export function useSetPrimaryDocumentExample(docType: string = 'demand_letter') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (exampleId: number) => {
      await api.post(`/firm-settings/examples/${exampleId}/primary`);
      return exampleId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-examples', docType] });
    },
  });
}

export function useMissingStatementGaps(caseId: string | undefined) {
  return useQuery<{
    gaps: StatementGap[];
    total_accounts_with_gaps: number;
    total_transfer_destinations: number;
    total_trailing_gaps: number;
    total_leading_gaps: number;
    total_balance_discontinuities: number;
    total_incomplete_copies: number;
    routing: {
      opposing: number;
      client: number;
      subpoena: number;
      resolve_ownership_first: number;
    };
  }>({
    queryKey: ['missing-gaps', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/missing-statement-gaps`);
      return data;
    },
    enabled: !!caseId,
  });
}

export function useDemandLetterSettings(caseId: string | undefined) {
  return useQuery<DemandLetterSettings>({
    queryKey: ['demand-letter-settings', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/demand-letter-settings`);
      return data;
    },
    enabled: !!caseId,
  });
}

export function useUpdateDemandLetterSettings(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (settings: Partial<DemandLetterSettings>) => {
      const { data } = await api.put(`/cases/${caseId}/demand-letter-settings`, settings);
      return data as DemandLetterSettings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['demand-letter-settings', caseId] });
    },
  });
}

interface GenerateRequest {
  selected_items?: Array<{ account_label: string; gap_type: string }>;
  notice_days?: number;
  delivery_method?: string;
  intro_override?: string | null;
}

export function useGenerateDemandLetter(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body?: GenerateRequest) => {
      const { data } = await api.post(`/cases/${caseId}/demand-letter`, body ?? {});
      return data as DemandLetterMeta;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['demand-letters', caseId] });
    },
  });
}

export function useGenerateRecordsRequests(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/cases/${caseId}/records-requests`);
      return data as DemandLetterMeta & { item_count: number };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['demand-letters', caseId] });
    },
  });
}

export function useDemandLetters(caseId: string | undefined) {
  return useQuery<{ letters: DemandLetterMeta[] }>({
    queryKey: ['demand-letters', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/demand-letters`);
      return data;
    },
    enabled: !!caseId,
  });
}

export function useDemandLetterPreview(letterId: number | null) {
  return useQuery<{ title: string; html: string }>({
    queryKey: ['demand-letter-preview', letterId],
    queryFn: async () => {
      const { data } = await api.get(`/demand-letters/${letterId}/preview`);
      return data;
    },
    enabled: letterId != null,
    staleTime: 60_000,
  });
}

export function useDeleteDemandLetter(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (letterId: number) => {
      await api.delete(`/demand-letters/${letterId}`);
      return letterId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['demand-letters', caseId] });
    },
  });
}
